import React, { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import api from "../../services/api"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"
import { Checkbox } from "../ui/checkbox"
import { Label } from "../ui/label"
import { Separator } from "../ui/separator"
import { DeleteConfirmDialog } from "../ConfirmDialog"
import { Settings, Trash2, Plus, AlertTriangle, Bell } from "lucide-react"

function AdminPanel({ adminOnly, onAdminOnlyChange }) {
  const queryClient = useQueryClient()
  const [newAdminId, setNewAdminId] = useState("")
  const [noticeDraft, setNoticeDraft] = useState("")
  const [noticeTitle, setNoticeTitle] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Fetch admin IDs
  const { data: adminData } = useQuery({
    queryKey: ["admin-ids"],
    queryFn: () => api.getAdminIds(),
  })
  const adminIds = adminData?.adminIds || []

  // Fetch notice
  const { data: notice } = useQuery({
    queryKey: ["notice"],
    queryFn: () => api.getNotice(),
  })

  // Mutations
  const addAdminMutation = useMutation({
    mutationFn: (id) => api.addAdminId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ids"] })
      setNewAdminId("")
      toast.success("Admin ID added successfully")
    },
    onError: (error) => {
      toast.error(`Failed to add admin ID: ${error.message}`)
    },
  })

  const removeAdminMutation = useMutation({
    mutationFn: (id) => api.removeAdminId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ids"] })
      toast.success("Admin ID removed")
    },
    onError: (error) => {
      toast.error(`Failed to remove admin ID: ${error.message}`)
    },
  })

  const deleteAllDataMutation = useMutation({
    mutationFn: () => api.deleteAdminData(),
    onSuccess: (data) => {
      queryClient.invalidateQueries()
      setDeleteDialogOpen(false)
      toast.success(`Deleted ${data.deletedCount} records`)
    },
    onError: (error) => {
      toast.error(`Failed to delete data: ${error.message}`)
    },
  })

  const publishNoticeMutation = useMutation({
    mutationFn: ({ message, title }) => api.setNotice(message, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notice"] })
      toast.success("Notice published successfully")
    },
    onError: (error) => {
      toast.error(`Failed to publish notice: ${error.message}`)
    },
  })

  const clearNoticeMutation = useMutation({
    mutationFn: () => api.clearNotice(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notice"] })
      setNoticeDraft("")
      setNoticeTitle("")
      toast.success("Notice cleared")
    },
    onError: (error) => {
      toast.error(`Failed to clear notice: ${error.message}`)
    },
  })

  const handleAddAdminId = (e) => {
    e.preventDefault()
    if (!newAdminId.trim()) return
    addAdminMutation.mutate(newAdminId.trim())
  }

  const handlePublishNotice = () => {
    if (!noticeDraft.trim()) {
      toast.error("Message cannot be empty")
      return
    }
    publishNoticeMutation.mutate({
      message: noticeDraft.trim(),
      title: noticeTitle.trim(),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Admin Controls
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Admin IDs Section */}
          <div>
            <h3 className="text-base font-semibold mb-3">Admin IDs</h3>
            <div className="border rounded-md p-2 max-h-40 overflow-y-auto mb-3">
              {adminIds.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2">No admin IDs</p>
              ) : (
                adminIds.map((id) => (
                  <div
                    key={id}
                    className="flex justify-between items-center p-2 odd:bg-muted/50 rounded"
                  >
                    <span className="text-sm font-mono truncate">{id}</span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => removeAdminMutation.mutate(id)}
                      disabled={removeAdminMutation.isPending}
                    >
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleAddAdminId} className="flex gap-2">
              <Input
                type="text"
                value={newAdminId}
                onChange={(e) => setNewAdminId(e.target.value)}
                placeholder="Enter new admin ID"
                className="flex-1"
                disabled={addAdminMutation.isPending}
              />
              <Button
                type="submit"
                disabled={addAdminMutation.isPending || !newAdminId.trim()}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </form>
          </div>

          {/* Actions Section */}
          <div>
            <h3 className="text-base font-semibold mb-3">Actions</h3>
            <div className="flex items-center space-x-2 mb-4">
              <Checkbox
                id="adminOnly"
                checked={adminOnly}
                onCheckedChange={onAdminOnlyChange}
              />
              <Label htmlFor="adminOnly">Include admin data</Label>
            </div>

            <Separator className="my-4" />

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Danger Zone
              </h4>
              <DeleteConfirmDialog
                trigger={
                  <Button variant="destructive" className="w-full">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All Admin Data
                  </Button>
                }
                title="Delete all admin data?"
                description="This will permanently delete all workout data associated with admin IDs. This action cannot be undone."
                onConfirm={() => deleteAllDataMutation.mutate()}
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
              />
            </div>
          </div>

          {/* Notice Section */}
          <div>
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Emergency Notice
            </h3>
            <Input
              type="text"
              value={noticeTitle}
              onChange={(e) => setNoticeTitle(e.target.value)}
              placeholder="Optional title (shown as alert title)"
              className="mb-2"
              disabled={publishNoticeMutation.isPending}
            />
            <Textarea
              value={noticeDraft}
              onChange={(e) => setNoticeDraft(e.target.value)}
              rows={4}
              placeholder="Type the one-time message to display to all users"
              className="mb-2"
              disabled={publishNoticeMutation.isPending}
            />
            <div className="flex gap-2">
              <Button
                onClick={handlePublishNotice}
                disabled={publishNoticeMutation.isPending || !noticeDraft.trim()}
              >
                Publish Notice
              </Button>
              <Button
                variant="secondary"
                onClick={() => clearNoticeMutation.mutate()}
                disabled={clearNoticeMutation.isPending}
              >
                Clear Notice
              </Button>
            </div>
            {notice && (
              <p className="text-xs text-muted-foreground mt-2">
                Current notice id:{" "}
                <code className="bg-muted px-1 rounded">{notice.id}</code>
                {notice.updatedAt && (
                  <> · updated {new Date(notice.updatedAt).toLocaleString()}</>
                )}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default AdminPanel
